import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber } from "./numberInput";
import { scaleRecipe } from "./recipe";

const TOOL_INFO_CONTENT = {
  what: `Scales a recipe's ingredient quantities up or down to match a different number of servings, so you don't have to do the multiplication or division by hand for every ingredient. It understands fractions (1/2, 1 1/2, ½), ranges (2-3) and package sizes such as 1 (14 oz) can.`,
  how: `Enter your ingredients and their original quantities, set the original and target serving counts, and every quantity recalculates automatically. Only the amount at the start of each line is scaled, so sizes in brackets stay as they are.`,
  tip: `Small cup and litre amounts are shown as tbsp + tsp or ml. Scaling works cleanly for most ingredients, but things like baking times, pan sizes, and spice levels don't always scale linearly — treat the scaled quantities as a strong starting point, not an exact guarantee for baked goods specifically.`,
};

export default function RecipeScaler() {
  const [recipe, setRecipe] = useState("2 cups flour\n1 cup sugar\n3 eggs\n1/2 cup butter\n1 1/2 tsp vanilla\n1 (14 oz) can tomatoes");
  const [originalServings, setOriginalServings] = useState("4");
  const [newServings, setNewServings] = useState("6");
  const [copied, setCopied] = useState(false);

  const o = parseNumber(originalServings), n = parseNumber(newServings);
  const result = o === null || n === null ? { error: "Enter both serving numbers (whole or decimal, more than 0)." } : scaleRecipe(recipe, o, n);
  const lines = Array.isArray(result) ? result : null;
  const factor = o && n && o > 0 && n > 0 ? n / o : null;
  const plain = lines ? lines.map((l) => l.text).join("\n") : "";

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });

  const copy = async () => {
    try { await navigator.clipboard.writeText(plain); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard unavailable */ }
  };

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div><Label>Recipe Ingredients (one per line)</Label><Textarea aria-label="Recipe ingredients" value={recipe} onChange={e => setRecipe(e.target.value)} className="min-h-[200px] font-mono" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Original Servings</Label><Input inputMode="decimal" aria-label="Original servings" value={originalServings} onChange={e => setOriginalServings(e.target.value)} /></div>
            <div><Label>New Servings</Label><Input inputMode="decimal" aria-label="New servings" value={newServings} onChange={e => setNewServings(e.target.value)} /></div>
          </div>
          <div className="flex gap-2 flex-wrap items-center text-xs text-muted-foreground">
            <span>Or scale by a factor:</span>
            {[0.5, 2, 3].map((f) => (
              <Button key={f} type="button" variant="outline" size="sm" className="h-7 px-2" onClick={() => { const base = o && o > 0 ? o : 4; setNewServings(String(Math.round(base * f * 100) / 100)); }}>×{f}</Button>
            ))}
          </div>
          {factor && <p className="text-xs text-muted-foreground" data-testid="rs-factor">Everything is multiplied by {Math.round(factor * 1000) / 1000}.</p>}
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Scaled Recipe ({newServings} servings)</Label>
            <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={copy} disabled={!lines || blocked}>{copied ? "Copied" : "Copy"}</Button>
          </div>
          {blocked ? <pre className="mt-2 p-4 rounded-lg border bg-card text-sm font-mono min-h-[200px]">—</pre>
            : lines ? (
              <div className="mt-2 p-4 rounded-lg border bg-card text-sm font-mono min-h-[200px] space-y-0.5" data-testid="rs-output">
                {lines.map((l, i) => (
                  <div key={i} className="whitespace-pre-wrap">{l.text || "\u00a0"}{l.note && <span className="block text-xs font-sans text-muted-foreground">↳ {l.note}</span>}</div>
                ))}
              </div>
            ) : <p className="mt-2 p-4 rounded-lg border text-sm text-destructive">{(result as { error: string }).error}</p>}
        </div>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
